import React, { useState } from 'react';
import { CircularProgress, FormControlLabel, FormGroup, Grid, IconButton, makeStyles, Typography, Radio, RadioGroup } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import TableChartIcon from '@material-ui/icons/TableChart';
import { useDispatch } from 'react-redux';
import allActions from '../../../actions';
import { ReportAPI } from '../../../API/report';
import { unoTheme } from '../../../assets/styles/unoTheme';
import UnoButton from '../../utils/UnoButton';
import { downloadBlob } from '../../../controllers/ExcelController';
import { getMonths, getMonthName } from '../../utils/utils';
import MySelect2 from '../../utils/MySelect2';
import MyAutoComplete from '../../utils/MyAutoComplete';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    scrollContainer: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        borderBottom: 'solid 1px rgba(0, 0, 0, 0.12)',
        backgroundColor: unoTheme.hoverTable,
        position: 'relative',
        flexShrink: 0,
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    title: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#1C2228'
    },
    subtitle: {
        fontSize: '14px',
        color: unoTheme.secondaryColor,
    },
    content: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    checkboxGroup: {
        width: '100%',
    },
    checkboxContainer: {
        backgroundColor: unoTheme.hoverTable,
        borderRadius: '12px',
        padding: theme.spacing(2),
    },
    checkboxItem: {
        marginBottom: theme.spacing(0.5),
        width: '100%',
        '& .MuiFormControlLabel-label': {
            fontSize: '14px',
            color: '#1C2228',
        },
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(2),
        top: '50%',
        transform: 'translateY(-50%)',
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#1C2228',
        marginBottom: theme.spacing(2),
    },
    footer: {
        marginTop: 'auto',
        padding: theme.spacing(3),
        borderTop: 'solid 1px rgba(0, 0, 0, 0.08)',
        flexShrink: 0,
        backgroundColor: '#fff',
    },
    overlay: {
        zIndex: 3,
        backgroundColor: 'white',
        position: 'absolute',
        opacity: 0.85,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        color: unoTheme.mainColor,
        alignItems: 'center',
        justifyContent: 'center',
        '& .progress': {
            color: unoTheme.mainColor
        }
    },
}));


export default function BackofficeReportGenerator({ content }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const {
        data: {
            year,
            statusesDair,
            advisorId,
            advisors,
            selectedAdvisor: initialAdvisor,
            consultingId,
            clientSearch,
        }
    } = content;

    const [reportMonth, setReportMonth] = useState(new Date().getMonth());
    const [selectedAdvisor, setSelectedAdvisor] = useState(initialAdvisor || null);
    const [reportFormat, setReportFormat] = useState('xlsx');

    const [selectedStatusIds, setSelectedStatusIds] = useState([]);
    const [loading, setLoading] = useState(false);

    const isAllSelected = selectedStatusIds.length === 0;
    const selectedMonthLabel = getMonthName(reportMonth + 1);

    const handleClose = () => {
        dispatch(allActions.rightContentActions.setRightContent(null));
    };

    const toggleStatus = (statusId) => {
        setSelectedStatusIds((current) => {
            if (current.includes(statusId)) {
                return current.filter((id) => id !== statusId);
            }

            return [...current, statusId];
        });
    };

    const handleSelectAll = () => {
        setSelectedStatusIds([]);
    };

    const formatStatusLabel = (label) => {
        if (!label) return '';
        const normalized = label.toLowerCase();
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const handleGenerate = async () => {
        setLoading(true);

        const response = await ReportAPI.getBackofficeReport(
            reportMonth + 1,
            year,
            selectedStatusIds,
            selectedAdvisor && selectedAdvisor.id ? selectedAdvisor.id : null,
            consultingId,
            clientSearch || null,
            reportFormat,
        );

        if (response.success && response.body && response.body.pathToReport) {
            const downloadResponse = await ReportAPI.downloadBackofficeReport(response.body.reportId);

            if (downloadResponse.success && downloadResponse.body && downloadResponse.body.blob) {
                const fileName = reportFormat === 'pdf' 
                    ? (downloadResponse.body.fileName || 'relatorio_backoffice.pdf')
                    : (downloadResponse.body.fileName || 'relatorio_backoffice.xlsx');
                downloadBlob(downloadResponse.body.blob, fileName);
            } else {
                dispatch(allActions.mainAlertActions.setMainAlert({
                    show: true,
                    message: 'Relatório gerado, mas houve falha no download autenticado.',
                    type: 'warning'
                }));
            }

            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                message: 'Relatório gerado com sucesso!',
                type: 'success'
            }));
        } else {
            const errorMsg = response.body && response.body.message
                ? response.body.message
                : 'Erro ao gerar relatório. Verifique se há comentários no mês selecionado.';
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                message: errorMsg,
                type: 'warning'
            }));
        }

        setLoading(false);
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.headerContent}>
                    <span className={classes.title}>Relatório Backoffice</span>
                    <span className={classes.subtitle}>Selecione os status desejados para {selectedMonthLabel}/{year}</span>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <MySelect2
                            style={{ minWidth: '120px' }}
                            id={'generator-month-select'}
                            value={reportMonth}
                            onChange={(e) => setReportMonth(parseInt(e.target.value))}
                            options={getMonths().map((row, index) => (
                                <option key={'gen-month-' + index} value={index}>
                                    {row}
                                </option>
                            ))}
                        />
                        {advisors && advisors.length > 0 && (
                            <div style={{ paddingTop: "4px" }}>
                                <MyAutoComplete
                                    id={'generator-advisor-select'}
                                    options={advisors}
                                    autoComplete
                                    customWidth={'200px'}
                                    value={selectedAdvisor}
                                    onChange={(event, newValue) => setSelectedAdvisor(newValue)}
                                    label={'Consultor'}
                                    placeholder={"Selecione o consultor"}
                                    getOptionLabel={(option) => option.name.toUpperCase()}
                                    getOptionSelected={(option, value) => option.id === value.id}
                                    renderOption={(option) => (
                                        <React.Fragment key={'gen-advisor-' + option.id}>
                                            <span>{option.name.toUpperCase()}</span>
                                        </React.Fragment>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <IconButton className={classes.closeButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </div>

            <div className={classes.scrollContainer}>
                <div className={classes.content}>

                <div className={classes.checkboxContainer} style={{ marginBottom: '16px' }}>
                    <Typography variant="subtitle2" className={classes.sectionTitle} style={{ marginBottom: '12px', marginTop: 0 }}>
                        Formato do Relatório
                    </Typography>
                    <RadioGroup
                        row
                        value={reportFormat}
                        onChange={(e) => setReportFormat(e.target.value)}
                        style={{ justifyContent: 'center', gap: '24px' }}
                    >
                        <FormControlLabel
                            value="xlsx"
                            control={<Radio color="primary" />}
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <TableChartIcon style={{ fontSize: 20 }} />
                                    <span>Excel (.xlsx)</span>
                                </div>
                            }
                            style={{ marginRight: 0 }}
                        />
                        <FormControlLabel
                            value="pdf"
                            control={<Radio color="primary" />}
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <PictureAsPdfIcon style={{ fontSize: 20 }} />
                                    <span>PDF (A4)</span>
                                </div>
                            }
                            style={{ marginRight: 0 }}
                        />
                    </RadioGroup>
                </div>

                <div className={classes.checkboxContainer}>
                    <FormGroup className={classes.checkboxGroup}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControlLabel
                                    className={classes.checkboxItem}
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={isAllSelected}
                                            onChange={handleSelectAll}
                                        />
                                    }
                                    label="Todos"
                                />
                            </Grid>

                            {(statusesDair || []).map((status) => (
                                <Grid item xs={12} sm={6} md={4} key={'backoffice-status-' + status.id}>
                                    <FormControlLabel
                                        className={classes.checkboxItem}
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={selectedStatusIds.includes(status.id)}
                                                onChange={() => toggleStatus(status.id)}
                                            />
                                        }
                                        label={formatStatusLabel(status.name)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>
                </div>
            </div>
            </div>

            <div className={classes.footer}>
                <UnoButton
                    startIcon={<GetAppIcon />}
                    onClick={handleGenerate}
                    disabled={loading}
                    type='primary'>
                    Gerar Relatório
                </UnoButton>
            </div>

            {loading ? (
                <div className={classes.overlay}>
                    <CircularProgress className="progress" />
                </div>
            ) : null}
        </div>
    );
}
